import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { BackdropLoader, Icon, SimpleModal, toast } from '@livable/saas-react-components';
import { Autocomplete, Button, IconButton, TextField, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import DropFileUploader from 'src/components/shared/DropFileUploader';
import { useStore } from 'src/stores';
import useStyles from './uploadBillModalStyles';

/** The service rejects a batch larger than this. */
const MAX_FILES = 10;
const getPropertyLabel = property => property.propertyAddress || property.propertyName || property.propertyId;
const getUtilitiesLabel = property => {
  var _property$utilities;
  const utilityTypes = [...new Set(((_property$utilities = property.utilities) !== null && _property$utilities !== void 0 ? _property$utilities : []).map(utility => utility.utilityType).filter(Boolean))];
  return utilityTypes.length ? utilityTypes.join(', ') : 'No utilities configured';
};
const formatFileSize = size => "".concat((size / 1048576).toFixed(2), " MB");

/**
 * Picks the bill files and, per file, the property it belongs to. Every file lands as its own bill, so
 * a property is chosen per file rather than once for the batch.
 */
export const UploadBillModal = observer(_ref => {
  let isOpen = _ref.isOpen,
    handleClose = _ref.handleClose;
  const classes = useStyles();
  const store = useStore();

  /** One entry per attached file: `{ file, propertyId }`. */
  const _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    attachments = _useState2[0],
    setAttachments = _useState2[1];
  // Remounts the uploader so picking the same file twice still fires a change event.
  const _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    uploaderKey = _useState4[0],
    setUploaderKey = _useState4[1];
  const properties = store.company.propertiesList;
  const propertiesAsync = store.company.getCompanyPropertiesAsync;
  const isSubmitting = store.companyBills.uploadBillsBatchAsync.inProgress;
  useEffect(() => {
    if (isOpen) return;
    setAttachments([]);
    setUploaderKey(key => key + 1);
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen || properties.length > 0 || propertiesAsync.inProgress || propertiesAsync.hasEverBeenRun) return;
    propertiesAsync.run({}, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  const propertyOptions = properties.slice().sort((a, b) => getPropertyLabel(a).localeCompare(getPropertyLabel(b), undefined, {
    numeric: true
  })).map(property => ({
    id: property.propertyId,
    label: getPropertyLabel(property),
    utilities: getUtilitiesLabel(property)
  }));
  const handleFilesAdded = selection => {
    const selectedFiles = (Array.isArray(selection) ? selection : [selection]).filter(file => file instanceof File);
    setUploaderKey(key => key + 1);
    if (!selectedFiles.length) return;
    setAttachments(current => {
      const isAlreadyAttached = file => current.some(attachment => attachment.file.name === file.name && attachment.file.size === file.size);
      const added = selectedFiles.filter(file => !isAlreadyAttached(file));
      const room = MAX_FILES - current.length;
      if (added.length > room) {
        toast('warning', {
          body: ["Up to ".concat(MAX_FILES, " files can be uploaded at once.")]
        });
      }
      return [...current, ...added.slice(0, Math.max(room, 0)).map(file => ({
        file,
        propertyId: ''
      }))];
    });
  };
  const handleRemoveFile = index => {
    setAttachments(current => current.filter((attachment, i) => i !== index));
  };
  const handleAssignProperty = (index, propertyId) => {
    setAttachments(current => current.map((attachment, i) => i === index ? _objectSpread(_objectSpread({}, attachment), {}, {
      propertyId
    }) : attachment));
  };

  /**
   * `PropertyIds` is positional and cannot hold a gap, so files with a property go up in one batch and
   * the ones left to address matching in another.
   */
  const buildBatches = () => {
    const assigned = attachments.filter(attachment => attachment.propertyId);
    const unassigned = attachments.filter(attachment => !attachment.propertyId);
    return [assigned.length ? {
      files: assigned.map(attachment => attachment.file),
      propertyIds: assigned.map(attachment => attachment.propertyId)
    } : null, unassigned.length ? {
      files: unassigned.map(attachment => attachment.file)
    } : null].filter(Boolean);
  };
  const handleUpload = async () => {
    // Uploaded files are not readable back from anywhere, so keep a session URL per file for previews.
    const fileUrls = new Map(attachments.map(attachment => [attachment.file, URL.createObjectURL(attachment.file)]));
    const landedFiles = [];
    for (const batch of buildBatches()) {
      // Sequential on purpose: asyncModel.run cancels the pending request of the same action.
      // eslint-disable-next-line no-await-in-loop
      const resp = await store.companyBills.uploadBillsBatchAsync.run(batch);
      const results = resp !== null && resp !== void 0 && resp.isSuccessful ? resp.response || [] : [];
      results.forEach((result, index) => {
        var _batch$propertyIds;
        const file = batch.files[index];
        const propertyId = ((_batch$propertyIds = batch.propertyIds) === null || _batch$propertyIds === void 0 ? void 0 : _batch$propertyIds[index]) || '';
        if (result.duplicate) {
          toast('warning', {
            body: ["".concat(result.fileName, " was already uploaded for this company.")]
          });
          return;
        }
        if (result.error || !result.billId) {
          toast('error', {
            title: 'Upload failed',
            body: ["".concat(result.fileName, ": ").concat(result.error || 'Unknown error')]
          });
          return;
        }
        const property = properties.find(item => item.propertyId === propertyId);
        store.uploadedBills.addUploadedBill({
          billId: result.billId,
          propertyId,
          propertyAddress: property ? getPropertyLabel(property) : '',
          fileName: result.fileName || (file === null || file === void 0 ? void 0 : file.name) || '',
          fileUrl: file ? fileUrls.get(file) : '',
          fileSizeMB: file ? file.size / 1048576 : 0
        });
        landedFiles.push(file);
      });
    }
    fileUrls.forEach((url, file) => {
      if (!landedFiles.includes(file)) URL.revokeObjectURL(url);
    });
    if (!landedFiles.length) return;
    toast('success', {
      body: ["".concat(landedFiles.length, " bill").concat(landedFiles.length === 1 ? '' : 's', " uploaded. ") + 'Extraction starts shortly and the rows update on their own.']
    });
    await store.companyBills.getCompanyBillsAsync.run({}, []);

    // Whatever did not land stays attached, so it can be corrected and sent again.
    const remaining = attachments.filter(attachment => !landedFiles.includes(attachment.file));
    setAttachments(remaining);
    if (!remaining.length) handleClose();
  };
  const assignedCount = attachments.filter(attachment => attachment.propertyId).length;
  const subTitle = attachments.length ? "".concat(attachments.length, " file").concat(attachments.length === 1 ? '' : 's', ", ").concat(assignedCount, " with a property") : 'No files attached';
  return /*#__PURE__*/React.createElement(SimpleModal, {
    title: "Upload bills",
    subTitle: subTitle,
    isOpen: isOpen,
    handleClose: handleClose
    // Without this the modal rebuilds its children on every render and the inputs lose focus.
    ,
    isMemoChildren: true,
    rightButton: /*#__PURE__*/React.createElement(Button, {
      variant: "contained",
      onClick: handleUpload,
      disabled: !attachments.length || isSubmitting,
      'custom-attribute': 'bills-upload-bill-modal-submit'
    }, isSubmitting ? 'Uploading…' : 'Upload bills')
  }, /*#__PURE__*/React.createElement(BackdropLoader, {
    isOpen: isSubmitting
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.section
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4",
    className: classes.sectionTitle
  }, "Attach bill files"), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.sectionHint
  }, "Up to ", MAX_FILES, " files per upload. Each file becomes its own bill."), /*#__PURE__*/React.createElement(DropFileUploader, {
    key: "upload-bill-files-".concat(uploaderKey),
    callback: handleFilesAdded,
    fileName: "",
    isOnlyUploader: true,
    multiple: true,
    noPreview: true
  })), attachments.length > 0 && /*#__PURE__*/React.createElement("div", {
    className: classes.section
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4",
    className: classes.sectionTitle
  }, "Assign properties"), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.sectionHint
  }, "Optional. A file left empty gets its property matched from the bill itself."), properties.length === 0 && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.emptyState
  }, propertiesAsync.inProgress ? 'Loading properties…' : 'No properties are available for this company yet.'), /*#__PURE__*/React.createElement("div", {
    className: classes.fileList
  }, attachments.map((attachment, index) => /*#__PURE__*/React.createElement("div", {
    className: classes.fileRow,
    key: "".concat(attachment.file.name, "-").concat(attachment.file.size)
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.fileMeta
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "file-text",
    state: "grey"
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.fileMetaText
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    title: attachment.file.name
  }, attachment.file.name), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.fileSize
  }, formatFileSize(attachment.file.size)))), /*#__PURE__*/React.createElement(Autocomplete, {
    className: classes.filePropertyField,
    options: propertyOptions,
    value: propertyOptions.find(option => option.id === attachment.propertyId) || null,
    onChange: (event, option) => handleAssignProperty(index, (option === null || option === void 0 ? void 0 : option.id) || ''),
    isOptionEqualToValue: (option, value) => option.id === value.id,
    disabled: properties.length === 0,
    renderInput: params => /*#__PURE__*/React.createElement(TextField, _extends({}, params, {
      label: "Property",
      variant: "outlined"
    })),
    renderOption: (props, option) => /*#__PURE__*/React.createElement("li", _extends({}, props, {
      key: option.id
    }), /*#__PURE__*/React.createElement("div", {
      className: classes.propertyItemText
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2"
    }, option.label), /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      className: classes.propertyUtilities
    }, option.utilities)))
  }), /*#__PURE__*/React.createElement(IconButton, {
    onClick: () => handleRemoveFile(index),
    "aria-label": "Remove ".concat(attachment.file.name),
    'custom-attribute': 'bills-upload-bill-modal-remove-file'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "trash"
  })))))));
});